import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Database, Users, Copy, CheckCircle, AlertTriangle, Clock,
    RefreshCw, ArrowRight, Shield, Activity, FileCheck, Search
} from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { dataQualityService } from '../../services/dataQualityService';
import { useAuth } from '../../context/AuthContext';

const DataQualityDashboard = () => {
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { hasPermission } = useAuth();

    const fetchDashboard = async (isRefresh = false) => {
        try {
            if (isRefresh) setRefreshing(true);
            else setLoading(true);

            const response = await dataQualityService.dashboard.get();
            if (response.success && response.data) {
                setDashboard(response.data);
                setError(null);
            }
        } catch (err) {
            console.error('Error fetching data quality dashboard:', err);
            setError('Failed to load data quality dashboard');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchDashboard();
    }, []);

    const formatNumber = (num) => {
        if (num === null || num === undefined) return '0';
        return new Intl.NumberFormat().format(num);
    };

    const formatPercentage = (num) => {
        if (num === null || num === undefined) return '0%';
        return `${Number(num).toFixed(1)}%`;
    };

    const StatCard = ({ title, value, subtitle, icon: Icon, color }) => {
        const colorClasses = {
            blue: 'bg-blue-100 text-blue-600',
            green: 'bg-green-100 text-green-600',
            purple: 'bg-purple-100 text-purple-600',
            yellow: 'bg-yellow-100 text-yellow-600',
            red: 'bg-red-100 text-red-600',
            indigo: 'bg-indigo-100 text-indigo-600',
            orange: 'bg-orange-100 text-orange-600',
            teal: 'bg-teal-100 text-teal-600',
        };

        return (
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
                        <Icon className="w-6 h-6" />
                    </div>
                </div>
                <div>
                    <p className="text-sm text-gray-500 mb-1">{title}</p>
                    {refreshing ? (
                        <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
                    ) : (
                        <>
                            <p className="text-2xl font-bold text-gray-900">{value}</p>
                            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
                        </>
                    )}
                </div>
            </div>
        );
    };

    const QuickActionCard = ({ title, description, icon: Icon, color, onClick }) => (
        <button
            onClick={onClick}
            className="bg-white rounded-lg p-5 shadow-sm border border-gray-200 hover:border-blue-300 hover:shadow-md transition text-left w-full"
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${color}`}>
                        <Icon className="w-5 h-5" />
                    </div>
                    <div>
                        <p className="font-medium text-gray-900">{title}</p>
                        <p className="text-sm text-gray-500">{description}</p>
                    </div>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400" />
            </div>
        </button>
    );

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-semibold text-gray-900">Data Quality Dashboard</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[...Array(8)].map((_, i) => (
                        <div key={i} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                            <div className="animate-pulse">
                                <div className="h-12 w-12 bg-gray-200 rounded-lg mb-4"></div>
                                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                                <div className="h-8 bg-gray-200 rounded"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <p className="text-red-600">{error}</p>
                <button
                    onClick={() => fetchDashboard()}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    const kycHealth = dashboard?.kycHealth;
    const clientHealth = dashboard?.clientHealth;
    const retailCompleteness = dashboard?.retailCompleteness;
    const corporateCompleteness = dashboard?.corporateCompleteness;

    // KYC Status pie chart data
    const kycPieData = kycHealth ? [
        { name: 'Pending', value: kycHealth.pendingKyc || 0, color: '#F59E0B' },
        { name: 'Verified', value: kycHealth.verifiedKyc || 0, color: '#3B82F6' },
        { name: 'Approved', value: kycHealth.approvedKyc || 0, color: '#10B981' },
        { name: 'Rejected', value: kycHealth.rejectedKyc || 0, color: '#EF4444' },
        { name: 'Expired', value: kycHealth.expiredKyc || 0, color: '#6B7280' },
    ].filter(d => d.value > 0) : [];

    // Completeness comparison bar chart
    const completenessBarData = (retailCompleteness || corporateCompleteness) ? [
        {
            name: 'Avg Completeness',
            Retail: retailCompleteness?.averageCompleteness || 0,
            Corporate: corporateCompleteness?.averageCompleteness || 0,
        },
        {
            name: 'Completion Rate',
            Retail: retailCompleteness?.completionRate || 0,
            Corporate: corporateCompleteness?.completionRate || 0,
        },
    ] : [];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-semibold text-gray-900">Data Quality Dashboard</h2>
                    {dashboard?.computedAt && (
                        <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Computed: {new Date(dashboard.computedAt).toLocaleString()}
                        </p>
                    )}
                </div>
                <button
                    onClick={() => fetchDashboard(true)}
                    disabled={refreshing}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center gap-2 disabled:opacity-50"
                >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    title="Duplicate Groups"
                    value={formatNumber(dashboard?.potentialDuplicateGroups)}
                    subtitle={`${formatNumber(dashboard?.clientsInDuplicateGroups)} clients affected`}
                    icon={Copy}
                    color="red"
                />
                <StatCard
                    title="Avg Completeness (Retail)"
                    value={formatPercentage(retailCompleteness?.averageCompleteness)}
                    subtitle={`${formatNumber(retailCompleteness?.completeProfiles)} / ${formatNumber(retailCompleteness?.totalClients)} complete`}
                    icon={CheckCircle}
                    color="green"
                />
                <StatCard
                    title="Avg Completeness (Corporate)"
                    value={formatPercentage(corporateCompleteness?.averageCompleteness)}
                    subtitle={`${formatNumber(corporateCompleteness?.completeProfiles)} / ${formatNumber(corporateCompleteness?.totalClients)} complete`}
                    icon={CheckCircle}
                    color="blue"
                />
                <StatCard
                    title="KYC Approval Rate"
                    value={formatPercentage(kycHealth?.kycApprovalRate)}
                    subtitle={`${formatNumber(kycHealth?.totalKycDocuments)} total documents`}
                    icon={Shield}
                    color="purple"
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    title="Stale Clients"
                    value={formatNumber(clientHealth?.staleClients)}
                    subtitle="No updates in 6+ months"
                    icon={Clock}
                    color="orange"
                />
                <StatCard
                    title="Clients Without Products"
                    value={formatNumber(clientHealth?.clientsWithNoProducts)}
                    subtitle="No assigned products"
                    icon={Users}
                    color="yellow"
                />
                <StatCard
                    title="Expired KYC"
                    value={formatNumber(kycHealth?.clientsWithExpiredKyc)}
                    subtitle="Clients with expired docs"
                    icon={AlertTriangle}
                    color="red"
                />
                <StatCard
                    title="Inactive with Products"
                    value={formatNumber(clientHealth?.inactiveWithActiveProducts)}
                    subtitle="Inactive clients holding products"
                    icon={Activity}
                    color="indigo"
                />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* KYC Status Pie Chart */}
                {kycPieData.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">KYC Status Breakdown</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={kycPieData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={3}
                                    dataKey="value"
                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                >
                                    {kycPieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value) => formatNumber(value)} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {/* Completeness Bar Chart */}
                {completenessBarData.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Completeness: Retail vs Corporate</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={completenessBarData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                                <Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} />
                                <Legend />
                                <Bar dataKey="Retail" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="Corporate" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Database className="w-5 h-5" />
                    Quick Actions
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <QuickActionCard
                        title="Review Duplicates"
                        description={`${formatNumber(dashboard?.potentialDuplicateGroups)} groups found`}
                        icon={Copy}
                        color="bg-red-100 text-red-600"
                        onClick={() => navigate('/data-quality/duplicates')}
                    />
                    <QuickActionCard
                        title="Profile Completeness"
                        description="View client completion rates"
                        icon={CheckCircle}
                        color="bg-green-100 text-green-600"
                        onClick={() => navigate('/data-quality/completeness')}
                    />
                    <QuickActionCard
                        title="Data Issues"
                        description="View quality issues and warnings"
                        icon={AlertTriangle}
                        color="bg-yellow-100 text-yellow-600"
                        onClick={() => navigate('/data-quality/issues')}
                    />
                    {hasPermission('DATA_QUALITY:WRITE') && (
                        <QuickActionCard
                            title="Cleanup Tools"
                            description="Standardize and fix client data"
                            icon={Search}
                            color="bg-blue-100 text-blue-600"
                            onClick={() => navigate('/data-quality/cleanup')}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default DataQualityDashboard;
